import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import Button from "../../components/common/Button";
import DateFilterField, {
  toDateInputValue,
} from "../../components/reporting/DateFilterField";
import FulfillmentOrders from "../fulfillment/FulfillmentOrders";
import { useExportFulfillmentOrdersMutation } from "../../store/api";
import { downloadBlob } from "../../utils/downloadFile";

const getDefaultStartDate = () => {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth(), 1, 0, 0, 0, 0);
};

const getDefaultEndDate = () => new Date();

const isDateInputValue = (value) => {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value || "")) return false;
  const [year, month, day] = value.split("-").map(Number);
  const parsed = new Date(year, month - 1, day);
  return toDateInputValue(parsed) === value;
};

const toStartOfDay = (value) => {
  if (!value) return null;
  const [year, month, day] = value.split("-").map(Number);
  return new Date(year, month - 1, day, 0, 0, 0, 0);
};

const toEndOfDay = (value) => {
  if (!value) return null;
  const [year, month, day] = value.split("-").map(Number);
  return new Date(year, month - 1, day, 23, 59, 59, 999);
};

const getDateFromParams = (searchParams, key, parser, fallback) => {
  const value = searchParams.get(key);
  return isDateInputValue(value) ? parser(value) : fallback();
};

const FulfillmentOrdersReport = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [status, setStatus] = useState(() => searchParams.get("status") || "");
  const [search, setSearch] = useState(() => searchParams.get("search") || "");
  const [startDate, setStartDate] = useState(() =>
    getDateFromParams(searchParams, "from", toStartOfDay, getDefaultStartDate),
  );
  const [endDate, setEndDate] = useState(() =>
    getDateFromParams(searchParams, "to", toEndOfDay, getDefaultEndDate),
  );
  const [exportOrders, { isLoading: isExporting }] =
    useExportFulfillmentOrdersMutation();

  useEffect(() => {
    setStatus(searchParams.get("status") || "");
    setSearch(searchParams.get("search") || "");
    setStartDate(
      getDateFromParams(searchParams, "from", toStartOfDay, getDefaultStartDate),
    );
    setEndDate(
      getDateFromParams(searchParams, "to", toEndOfDay, getDefaultEndDate),
    );
  }, [searchParams]);

  const updateFilterParams = (patch) => {
    setSearchParams(
      (currentParams) => {
        const nextParams = new URLSearchParams(currentParams);

        Object.entries(patch).forEach(([key, value]) => {
          if (value) {
            nextParams.set(key, value);
          } else {
            nextParams.delete(key);
          }
        });

        return nextParams;
      },
      { replace: true },
    );
  };

  const handleStatusChange = (value) => {
    setStatus(value);
    updateFilterParams({ status: value });
  };

  const handleSearchChange = (value) => {
    setSearch(value);
    updateFilterParams({ search: value });
  };

  const handleStartDateChange = (value) => {
    setStartDate(value);
    updateFilterParams({ from: toDateInputValue(value) });
  };

  const handleEndDateChange = (value) => {
    setEndDate(value);
    updateFilterParams({ to: toDateInputValue(value) });
  };

  const handleReset = () => {
    const nextStartDate = getDefaultStartDate();
    const nextEndDate = getDefaultEndDate();

    setStatus("");
    setSearch("");
    setStartDate(nextStartDate);
    setEndDate(nextEndDate);
    updateFilterParams({
      status: "",
      search: "",
      from: "",
      to: "",
    });
  };

  const handleDownload = async () => {
    const result = await exportOrders({
      status: status || undefined,
      search: search || undefined,
      from: startDate ? startDate.toISOString() : undefined,
      to: endDate ? endDate.toISOString() : undefined,
    });
    if (!("data" in result)) {
      return;
    }

    const contentDisposition = result?.meta?.response?.headers?.get(
      "content-disposition",
    );
    downloadBlob({
      blob: result.data,
      filename: `fulfillment-orders-${new Date().toISOString().slice(0, 10)}.xlsx`,
      contentDisposition,
    });
  };

  return (
    <div className="space-y-2">
      <div className="mb-0 flex items-center gap-2 px-1">
        <h1 className="text-2xl font-semibold tracking-tight text-slate-800 dark:text-slate-100">
          Orders
        </h1>
        <div className="ml-auto">
          <Button size="sm" className="h-9 px-3 text-sm" onClick={handleDownload} disabled={isExporting}>
            {isExporting ? "Downloading..." : "Download"}
          </Button>
        </div>
      </div>

      <div className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-700 dark:bg-slate-800">
        <div className="flex flex-wrap items-end gap-4">
          <label className="relative block min-w-[260px] flex-1">
            <span className="mb-1 block text-xs font-medium text-slate-700 dark:text-slate-300">
              Search
            </span>
            <span className="relative block">
              <span className="pointer-events-none absolute inset-y-0 left-3 flex items-center text-slate-400 dark:text-slate-500">
                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="m21 21-4.35-4.35m1.6-4.15a7 7 0 1 1-14 0 7 7 0 0 1 14 0Z" />
                </svg>
              </span>
              <input
                type="text"
                value={search}
                onChange={(e) => handleSearchChange(e.target.value)}
                placeholder="Enter the order number or order ID"
                className="h-9 w-full rounded-lg border border-slate-300 bg-white pl-9 pr-3 text-sm text-slate-900 shadow-sm outline-none transition focus:border-slate-400 focus:ring-2 focus:ring-indigo-500 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-100"
              />
            </span>
          </label>

          <DateFilterField
            label="From"
            value={startDate}
            max={toDateInputValue(endDate)}
            onChange={handleStartDateChange}
            parseValue={toStartOfDay}
          />

          <DateFilterField
            label="To"
            value={endDate}
            min={toDateInputValue(startDate)}
            onChange={handleEndDateChange}
            parseValue={toEndOfDay}
          />

          <label className="min-w-[190px]">
            <span className="mb-1 block text-xs font-medium text-slate-700 dark:text-slate-300">
              Status
            </span>
            <select
              value={status}
              onChange={(e) => handleStatusChange(e.target.value)}
              className="h-9 w-full rounded-lg border border-slate-300 bg-white px-3 text-sm text-slate-900 shadow-sm outline-none transition focus:border-slate-400 focus:ring-2 focus:ring-indigo-500 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-100"
            >
              <option value="">All</option>
              <option value="PREPARING">PREPARING</option>
              <option value="CAPTURED">CAPTURED</option>
              <option value="PAYMENT_FAILED">PAYMENT_FAILED</option>
              <option value="COMPLETED">COMPLETED</option>
            </select>
          </label>

          <div className="ml-auto">
            <Button
              size="sm"
              variant="secondary"
              className="h-9 px-5 text-sm"
              onClick={handleReset}
            >
              Reset
            </Button>
          </div>
        </div>
      </div>

      <FulfillmentOrders
        embedded
        compact
        showFilters={false}
        showDownloadButton={false}
        status={status}
        onStatusChange={handleStatusChange}
        search={search}
        onSearchChange={handleSearchChange}
        startDate={startDate}
        onStartDateChange={handleStartDateChange}
        endDate={endDate}
        onEndDateChange={handleEndDateChange}
      />
    </div>
  );
};

export default FulfillmentOrdersReport;
